import { PlayerMatchStats, SideFilter, HeadToHeadData } from "../types/match.types";

// Map images helper
export const mapImages: Record<string, string> = {
	Mirage: "/images/maps/de_mirage.png",
	Nuke: "/images/maps/de_nuke.png",
	Inferno: "/images/maps/de_inferno.png",
	Dust2: "/images/maps/de_dust2.png",
	Overpass: "/images/maps/de_overpass.png",
	Ancient: "/images/maps/de_ancient.png",
	Anubis: "/images/maps/de_anubis.png",
	Train: "/images/maps/de_train.png",
	Vertigo: "/images/maps/de_vertigo.png",
};

// Helper function to detect available data types
export const getAvailableDataTypes = (
	team1Players: PlayerMatchStats[],
	team2Players: PlayerMatchStats[]
) => {
	const allPlayers = [...team1Players, ...team2Players];

	// Check if side-specific data is available
	const hasSideData = allPlayers.some(
		(player) =>
			(player.tSideRoundsPlayed && player.tSideRoundsPlayed > 0) ||
			(player.ctSideRoundsPlayed && player.ctSideRoundsPlayed > 0)
	);

	// Check if head-to-head data is available
	const hasH2HData = allPlayers.some(
		(player) =>
			player.killsAgainstPlayers &&
			Object.keys(player.killsAgainstPlayers).length > 0
	);

	return {
		hasSideData,
		hasH2HData,
	};
};

// Helper function to get stats based on side filter
export const getPlayerStatsBySide = (
	player: PlayerMatchStats,
	side: SideFilter
): PlayerMatchStats => {
	if (side === "all") {
		return player;
	}

	if (side === "t") {
		// Return T-side stats, using the exact property names from API
		return {
			...player,
			kills: player.tSideKills ?? 0,
			awpKills: player.tSideAwpKills ?? 0,
			hsKills: player.tSideHsKills ?? 0,
			deaths: player.tSideDeaths ?? 0,
			assists: player.tSideAssists ?? 0,
			hs: player.tSideHS ?? 0,
			dmg: player.tSideDMG ?? 0,
			adr: player.tSideADR ?? 0,
			kd: player.tSideKD ?? 0,
			fk: player.tSideFK ?? 0,
			firstDeaths: player.tSideFirstDeaths ?? 0,
			kast: player.tSideKAST ?? 0,
			rating1: player.tSideRating1 ?? 0,
			rating2: player.tSideRating2 ?? 0,
			roundsPlayed: player.tSideRoundsPlayed ?? 0,
			pistolKills: player.tSidePistolKills ?? 0,
			entryKillsT: player.tSideEntryKills ?? 0,
		};
	}

	if (side === "ct") {
		// Return CT-side stats, using the exact property names from API
		return {
			...player,
			kills: player.ctSideKills ?? 0,
			awpKills: player.ctSideAwpKills ?? 0,
			hsKills: player.ctSideHsKills ?? 0,
			deaths: player.ctSideDeaths ?? 0,
			assists: player.ctSideAssists ?? 0,
			hs: player.ctSideHS ?? 0,
			dmg: player.ctSideDMG ?? 0,
			adr: player.ctSideADR ?? 0,
			kd: player.ctSideKD ?? 0,
			fk: player.ctSideFK ?? 0,
			firstDeaths: player.ctSideFirstDeaths ?? 0,
			kast: player.ctSideKAST ?? 0,
			rating1: player.ctSideRating1 ?? 0,
			rating2: player.ctSideRating2 ?? 0,
			roundsPlayed: player.ctSideRoundsPlayed ?? 0,
			pistolKills: player.ctSidePistolKills ?? 0,
		};
	}

	return player;
};

// Helper function to generate head-to-head data
export const generateHeadToHeadData = (
	players: PlayerMatchStats[],
	sideFilter: SideFilter
): HeadToHeadData[] => {
	const h2hData: HeadToHeadData[] = [];

	for (let i = 0; i < players.length; i++) {
		for (let j = i + 1; j < players.length; j++) {
			const player1 = players[i];
			const player2 = players[j];

			// Get kills based on side filter
			let killsAgainst1: Record<string, number> = {};
			let killsAgainst2: Record<string, number> = {};

			if (sideFilter === "all" || sideFilter === "h2h") {
				killsAgainst1 = player1.killsAgainstPlayers || {};
				killsAgainst2 = player2.killsAgainstPlayers || {};
			} else if (sideFilter === "t") {
				killsAgainst1 = player1.tSideKillsAgainstPlayers || {};
				killsAgainst2 = player2.tSideKillsAgainstPlayers || {};
			} else if (sideFilter === "ct") {
				killsAgainst1 = player1.ctSideKillsAgainstPlayers || {};
				killsAgainst2 = player2.ctSideKillsAgainstPlayers || {};
			}

			const kills1vs2 = killsAgainst1[player2.steamId] || 0;
			const kills2vs1 = killsAgainst2[player1.steamId] || 0;

			// Only add if there were actual head-to-head kills
			if (kills1vs2 > 0 || kills2vs1 > 0) {
				h2hData.push({
					player1,
					player2,
					kills1vs2,
					kills2vs1,
				});
			}
		}
	}

	// Sort by total duels (most duels first)
	return h2hData.sort(
		(a, b) => b.kills1vs2 + b.kills2vs1 - (a.kills1vs2 + a.kills2vs1)
	);
};